import { useState } from "react";
import { NavLink, Link, useParams } from "react-router-dom";
import { Menu, X, ExternalLink } from "lucide-react";
import { MINI_SITE_NAV } from "./context";
import DemoBadge from "../DemoBadge";
import ShareButton from "../share/ShareButton";
import { BrandLogo } from "../BrandLogo";

/** Sticky public mini-site navigation bar (desktop tabs + mobile drawer). */
export default function MiniSiteNav({ company, theme }) {
  const { slug } = useParams();
  const [open, setOpen] = useState(false);

  // Canonical slug for sharing: prefer the company's current public
  // slug, never the URL alias we may have been visited under.
  const canonicalSlug = (company.public_slug || slug || "").toLowerCase();
  const shareDescription =
    (company.public_description_full || company.description || "").trim().slice(0, 180)
    || `View ${company.name || "this business"} on ConfirmPH.`;

  const link = (to) =>
    `text-sm font-medium px-3 py-1.5 rounded-md transition whitespace-nowrap`;

  return (
    <header
      className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200"
      data-testid="ms-nav"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 gap-3">
          {/* Brand */}
          <Link
            to={`/rentals/${slug}/home`}
            className="flex items-center gap-2 min-w-0"
            data-testid="ms-nav-brand"
          >
            {company.public_logo_url ? (
              <img
                src={company.public_logo_url}
                alt=""
                className="w-7 h-7 rounded object-cover border border-slate-200 flex-shrink-0"
              />
            ) : (
              <span
                className="w-7 h-7 rounded grid place-items-center text-white text-xs font-bold flex-shrink-0"
                style={{ background: theme }}
              >
                {(company.name || "?")[0]}
              </span>
            )}
            <span className="font-display text-base font-semibold truncate" style={{ color: theme }}>
              {company.name}
            </span>
            {company.is_demo && (
              <DemoBadge variant="header" size="xs" className="inline-flex flex-shrink-0" testId="ms-nav-demo-badge" />
            )}
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-0.5">
            {MINI_SITE_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "home"}
                data-testid={`ms-nav-${n.to}`}
                className={({ isActive }) =>
                  `${link()} ${
                    isActive
                      ? "text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`
                }
                style={({ isActive }) => (isActive ? { background: theme } : undefined)}
              >
                {n.label}
              </NavLink>
            ))}
            <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />
            <ShareButton
              slug={canonicalSlug}
              businessName={company.name}
              description={shareDescription}
              variant="ghost"
              size="sm"
              testId="ms-share-btn"
              label="Share"
              className="ml-1"
            />
          </nav>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="ms-nav-mobile-toggle"
            className="md:hidden p-2 rounded hover:bg-slate-100"
            aria-label="Toggle navigation"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div className="md:hidden pb-3" data-testid="ms-nav-mobile">
            <nav className="flex flex-col gap-1">
              {MINI_SITE_NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "home"}
                  onClick={() => setOpen(false)}
                  data-testid={`ms-nav-mobile-${n.to}`}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded text-sm font-medium ${
                      isActive ? "text-white" : "text-slate-700 hover:bg-slate-100"
                    }`
                  }
                  style={({ isActive }) => (isActive ? { background: theme } : undefined)}
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/rentals"
                onClick={() => setOpen(false)}
                className="px-3 py-2 mt-1 rounded text-xs text-slate-500 inline-flex items-center gap-1 hover:bg-slate-100"
                data-testid="ms-nav-mobile-back"
              >
                <ExternalLink className="w-3 h-3" /> Back to ConfirmPH
              </Link>
              <div className="px-3 pt-2 mt-1 border-t border-slate-100">
                <ShareButton
                  slug={canonicalSlug}
                  businessName={company.name}
                  description={shareDescription}
                  variant="secondary"
                  size="md"
                  testId="ms-share-mobile-btn"
                  label="Share this listing"
                  className="w-full justify-center"
                />
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}

export function MiniSiteFooter({ theme }) {
  return (
    <footer className="border-t border-slate-200 bg-slate-50 mt-12" data-testid="ms-footer">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
        <div className="inline-flex items-center gap-2">
          <BrandLogo
            variant="icon"
            className="w-4 h-4"
            alt="ConfirmPH"
            testId="ms-footer-brand-icon"
          />
          <span>
            Booking website <strong>powered by</strong>{" "}
            <Link to="/" className="font-semibold hover:underline" style={{ color: theme }}>
              ConfirmPH
            </Link>
          </span>
        </div>
        <Link to="/rentals" className="hover:underline" data-testid="ms-footer-discover">
          Discover more rentals →
        </Link>
      </div>
    </footer>
  );
}
